import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../../auth/Auth';
import { getListMFAEnabled } from '../../auth/_requests';

interface MFAState {
  emailEnabled: boolean;
  phoneEnabled: boolean;
  emailMfaId: string;
  phoneMfaId: string;
  setEmailEnabled: (enabled: boolean) => void;
  setPhoneEnabled: (enabled: boolean) => void;
  loadingMfa: boolean;
  setLoadingMfa: (loadingMfa: boolean) => void;
  refreshMFA: () => void;
}

const MFAContext = createContext<MFAState | undefined>(undefined);

export const MFAProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { auth } = useAuth();
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [phoneEnabled, setPhoneEnabled] = useState(false);
  const [emailMfaId, setEmailMfaId] = useState('');
  const [phoneMfaId, setPhoneMfaId] = useState('');
  const [loadingMfa, setLoadingMfa] = useState(false);

  const fetchMFAList = async () => {
    try {
      setLoadingMfa(true);
      if (!auth?.data.token) return;
      const response = await getListMFAEnabled(auth.data.token);
      if (response?.data) {
        const emailMFA = response.data.find((mfa) => mfa.name === 'email');
        const phoneMFA = response.data.find((mfa) => mfa.name === 'phoneNumber');
        setEmailEnabled(String(emailMFA?.isEnabled) === 'true');
        setPhoneEnabled(String(phoneMFA?.isEnabled) === 'true');
        setEmailMfaId(emailMFA?.mfaId || '');
        setPhoneMfaId(phoneMFA?.mfaId || '');
      }
    } catch (error) {
      console.error('Error fetching MFA list:', error);
    } finally {
      setLoadingMfa(false);
    }
  };

  useEffect(() => {
    fetchMFAList();
  }, [auth?.data.token]);

  return (
    <MFAContext.Provider
      value={{
        emailEnabled,
        phoneEnabled,
        emailMfaId,
        phoneMfaId,
        setEmailEnabled,
        setPhoneEnabled,
        loadingMfa,
        setLoadingMfa,
        refreshMFA: fetchMFAList,
      }}
    >
      {children}
    </MFAContext.Provider>
  );
};

export const useMFA = () => {
  const context = useContext(MFAContext);
  if (!context) {
    throw new Error('useMFA must be used within an MFAProvider');
  }
  return context;
};
